import morph from "../helpers/morph";
import bindBloomreachEvents from "../helpers/bloomreach";

const ROOT_SELECTOR = "#MainContent";
const OPTION_SELECTOR = "product-form-options select[data-option-name]";
const FIRST_MEDIA_SELECTOR = "media-gallery [data-media-index]";
const KLAVIYO_REVIEWS_SELECTOR =
  "#fulfilled-reviews-all, #klaviyo-reviews-all, .fulfilled-star-rating-widget, .klaviyo-star-rating-widget";
const REVIEWS_TIMEOUT = 5000;
const HOVER_INTENT_DELAY = 80;
const PREFETCH_TTL = 30000;
const IMAGE_WAIT = 500;

let settleReviews = null;

const prefetched = new Map();

/**
 * Fetches and parses a colourway's page, and starts its first gallery image
 * loading. Reuses a recent prefetch of the same page.
 *
 * @param {string} url Product URL
 * @returns {Promise<Document>}
 */
function loadColourway(url) {
  const cached = prefetched.get(url);

  if (cached && cached.expires > Date.now()) return cached.request;

  const entry = { expires: Date.now() + PREFETCH_TTL };

  entry.request = fetch(url).then(async (response) => {
    if (!response.ok) throw new Error(response.statusText);

    const doc = new DOMParser().parseFromString(
      await response.text(),
      "text/html",
    );

    // The parsed document is inert. An imported copy of the <picture> picks
    // its source for this viewport and loads it, so the gallery's own image
    // comes from cache. Held on the entry so the load isn't dropped.
    const picture = doc.querySelector(`${FIRST_MEDIA_SELECTOR} picture`);
    if (picture) entry.image = document.importNode(picture, true);

    return doc;
  });

  entry.request.catch(() => prefetched.delete(url));
  prefetched.set(url, entry);

  return entry.request;
}

/**
 * Hands over a colourway's page for swapping in. Taken out of the cache, as
 * the swap rewrites the parsed document, and a later visit should be fresh.
 *
 * @param {string} url Product URL
 * @returns {Promise<Document>}
 */
function takeColourway(url) {
  const request = loadColourway(url);

  prefetched.delete(url);

  return request;
}

// Resolves once the incoming gallery's first image can paint, or after
// IMAGE_WAIT, so the outgoing image isn't swapped for a blank slide.
function galleryImageReady(fragment) {
  const image = fragment.querySelector(`${FIRST_MEDIA_SELECTOR} img`);

  if (!image) return Promise.resolve();

  return Promise.race([
    image.decode().catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, IMAGE_WAIT)),
  ]);
}

/**
 * Colourway swatches. Each colour is its own product, so a swatch click
 * fetches that product page and morphs it over the current one. Hovering or
 * focusing a swatch prefetches its page and first gallery image.
 *
 * Widgets whose state lives in JS (the media gallery's Swiper, the detail
 * tabs) carry `data-force-update` so the morph swaps them wholesale and they
 * re-initialise, rather than being patched out from under their scripts.
 */
class ProductSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this._onClick);
    this.addEventListener("pointerover", this._onPointerOver);
    this.addEventListener("pointerleave", this._onPointerLeave);
    this.addEventListener("focusin", this._onFocusIn);
    this._revealCurrent();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("pointerover", this._onPointerOver);
    this.removeEventListener("pointerleave", this._onPointerLeave);
    this.removeEventListener("focusin", this._onFocusIn);
    this._onPointerLeave();
    this._target = null;
  }

  _prefetchable(target) {
    const swatch = target.closest?.("[data-product-swatch]");

    if (!swatch || swatch.hasAttribute("aria-current")) return null;
    if (navigator.connection?.saveData) return null;

    return swatch;
  }

  // A mouse sweeping across the row shouldn't fetch every colour it passes.
  // Touch fires pointerover at the start of a tap, so it prefetches at once.
  _onPointerOver = (event) => {
    const swatch = this._prefetchable(event.target);

    if (swatch === this._hovered) return;

    clearTimeout(this._hoverTimeout);
    this._hovered = swatch;

    if (!swatch) return;

    const delay = event.pointerType === "mouse" ? HOVER_INTENT_DELAY : 0;

    this._hoverTimeout = setTimeout(
      () => loadColourway(swatch.href).catch(() => {}),
      delay,
    );
  };

  _onPointerLeave = () => {
    clearTimeout(this._hoverTimeout);
    this._hovered = null;
  };

  _onFocusIn = (event) => {
    const swatch = this._prefetchable(event.target);

    if (swatch) loadColourway(swatch.href).catch(() => {});
  };

  _onClick = (event) => {
    const swatch = event.target.closest("[data-product-swatch]");

    if (!swatch) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    if (swatch.hasAttribute("aria-current")) return;

    this._markCurrent(swatch);
    this._swap(swatch.href);
  };

  // Selects the swatch straight away, ahead of the page that confirms it.
  _markCurrent(swatch) {
    this.querySelectorAll("[data-product-swatch][aria-current]").forEach(
      (current) => current.removeAttribute("aria-current"),
    );
    swatch.setAttribute("aria-current", "page");
  }

  async _swap(url) {
    const root = document.querySelector(ROOT_SELECTOR);

    if (!root) {
      window.location.assign(url);
      return;
    }

    // Superseded swaps aren't aborted, as their request may be a shared
    // prefetch; each checks it's still the target before touching the page.
    this._target = url;

    const selections = this._optionSelections(root);

    this.setAttribute("aria-busy", "true");

    try {
      const doc = await takeColourway(url);
      const nextRoot = doc.querySelector(ROOT_SELECTOR);

      if (!nextRoot) throw new Error(`No ${ROOT_SELECTOR} in ${url}`);

      this._keepSwatchOrder(nextRoot);

      const next = document
        .createRange()
        .createContextualFragment(nextRoot.outerHTML);

      await galleryImageReady(next);

      if (this._target !== url) return;

      this._holdReviews(root);

      morph(root, next);
      bindBloomreachEvents();

      if (doc.title) document.title = doc.title;

      if (!window.Shopify?.designMode) {
        history.replaceState(history.state, "", url);
      }

      this._restoreSelections(root, selections);

      this._scrollToGallery(root);

      this._renderReviews(root);
    } catch (error) {
      if (this._target !== url) return;

      console.warn("[PDP] colourway swap failed", error);
      window.location.assign(url);
    } finally {
      if (this._target === url) {
        this._target = null;
        this.removeAttribute("aria-busy");
      }
    }
  }

  // Each page pins its own colour first. Swatches keep the order the page
  // landed with instead, so they don't shuffle with every colour switched to.
  _keepSwatchOrder(nextRoot) {
    const track = this.querySelector("[data-swatch-track]");
    const nextTrack = nextRoot.querySelector("[data-swatch-track]");

    if (!track || !nextTrack) return;

    const hrefOf = (item) =>
      item.querySelector("[data-product-swatch]")?.getAttribute("href");
    const nextItems = new Map(
      [...nextTrack.children].map((item) => [hrefOf(item), item]),
    );
    const ordered = [...track.children]
      .map((item) => nextItems.get(hrefOf(item)))
      .filter(Boolean);
    const added = [...nextItems.values()].filter(
      (item) => !ordered.includes(item),
    );

    nextTrack.replaceChildren(...ordered, ...added);
  }

  _optionSelections(root) {
    return [...root.querySelectorAll(OPTION_SELECTOR)]
      .filter((select) => select.value)
      .map((select) => [select.dataset.optionName, select.value]);
  }

  // The incoming page lands with its options unchosen, so values picked on
  // the previous colour are carried over wherever the new colour offers them.
  _restoreSelections(root, selections) {
    let changed = null;

    selections.forEach(([name, value]) => {
      const select = [...root.querySelectorAll(OPTION_SELECTOR)].find(
        (candidate) => candidate.dataset.optionName === name,
      );
      const option = [...(select?.options || [])].find(
        (candidate) => candidate.value === value && !candidate.disabled,
      );

      if (!option || select.value === value) return;

      select.value = value;
      changed = select;
    });

    changed?.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // On desktop a colourway with fewer images has a shorter gallery, and the
  // old scroll position can leave the form above the viewport. Starting the
  // new colourway at its first image has the user scroll down through it.
  // Only ever scrolls up. Instant, because the root element has smooth
  // scroll-behavior.
  _scrollToGallery(root) {
    const desktop = window.matchMedia(
      `(min-width: ${window.themeBreakpoints["tablet-wide"]}px)`,
    ).matches;
    const media = root.querySelector(FIRST_MEDIA_SELECTOR);

    if (!desktop || !media) return;

    // `scroll-margin-top` on the slide keeps it clear of the sticky header.
    const offset = parseFloat(getComputedStyle(media).scrollMarginTop) || 0;

    if (media.getBoundingClientRect().top >= offset) return;

    media.scrollIntoView({ behavior: "instant", block: "start" });
  }

  // Rendered widgets get only their data-id morphed, so the previous
  // product's reviews stay on screen (and hold their height) until
  // _renderReviews has the new ones ready.
  _holdReviews(root) {
    settleReviews?.();

    root.querySelectorAll(KLAVIYO_REVIEWS_SELECTOR).forEach((widget) => {
      if (widget.childElementCount) widget.dataset.attributesOnly = "data-id";
    });
  }

  // Klaviyo only renders into empty containers, and only on page load. The
  // morphed containers can't be reused: they still hold Klaviyo's Preact tree
  // for the previous product, so re-rendering into them patches detached
  // nodes and shows nothing. Each gets a fresh, hidden clone to render into,
  // swapped in once it has content. The clone goes before the original so a
  // getElementById on the shared id finds it.
  _renderReviews(root) {
    const widgets = [...root.querySelectorAll(KLAVIYO_REVIEWS_SELECTOR)];
    const pending = [];

    widgets.forEach((widget) => {
      const held = "attributesOnly" in widget.dataset;
      delete widget.dataset.attributesOnly;

      const clone = widget.cloneNode(false);

      // Nothing on screen to hold: swap the clone in straight away.
      if (!held || !window.klReviewsObject?.renderWidgets) {
        widget.replaceWith(clone);
        return;
      }

      clone.hidden = true;
      widget.before(clone);
      pending.push([widget, clone]);
    });

    if (!window.klReviewsObject?.renderWidgets) return;

    const reveal = ([widget, clone]) => {
      if (!widget.isConnected) return;

      widget.replaceWith(clone);
      clone.hidden = false;
    };

    const observer = new MutationObserver(() => {
      pending.filter(([, clone]) => clone.childElementCount).forEach(reveal);
    });

    pending.forEach(([, clone]) =>
      observer.observe(clone, { childList: true }),
    );

    // A product with no reviews may never render into a widget; give up
    // holding the old content rather than show another product's rating.
    const timeout = setTimeout(() => settleReviews?.(), REVIEWS_TIMEOUT);

    settleReviews = () => {
      settleReviews = null;
      clearTimeout(timeout);
      observer.disconnect();
      pending.forEach(reveal);
    };

    window.klReviewsObject.renderWidgets();
  }

  // On mobile the swatches are a single scrolling row; bring the current
  // colour into view without scrolling the page.
  _revealCurrent() {
    const track = this.querySelector("[data-swatch-track]");
    const current = this.querySelector("[aria-current]");

    if (!track || !current || track.scrollWidth <= track.clientWidth) return;

    const item = current.closest("li") || current;

    track.scrollLeft =
      item.offsetLeft - (track.clientWidth - item.offsetWidth) / 2;
  }
}

if (!customElements.get("product-swatches")) {
  customElements.define("product-swatches", ProductSwatches);
}
